// Steuerkategorie-Katalog (VB-1 / Ausgangsrechnungen)
//
// Reiner, seiteneffektfreier Katalog der bei der Rechnungserstellung wählbaren
// Steuerarten. Bildet die fachliche Auswahl (`InvoiceTaxCategoryKey`) auf die
// EN-16931-Werte (BT-151-Kategorie-Code, Befreiungsgrund BT-120/121, Satz-Constraint)
// ab. Die Tabelle ist die SoT für das, was das FE an das PHP-Backend durchreicht
// (`taxCategory`, `exemptionReason`) und was in der Vorschau als Pflicht-Hinweis
// gerendert wird.
//
// Verbindliche fachliche Grundlagen (READ-ONLY-Rulings, 2026-06-26):
//   - e-rechnung: solar-tax-category-ruling.md (BT-151-Tabelle, VETO-1..5)
//   - jurist:     jurist-tax-category-ruling.md (Pflicht-Wortlaute §§1.1-1.6, A1-A6)
//
// WICHTIG (legal-kritisch): Die deutschen `exemptionReasonText`-Wortlaute unten sind
// 1:1 aus dem jurist-Ruling übernommen und dürfen NICHT paraphrasiert werden
// (Rule J-1). Die DE-i18n-Strings (`public/locales/de/invoice.json` → `taxCategory.reasons`)
// spiegeln sie wortgleich; bei Änderung beide Stellen synchron halten.

/**
 * EN-16931 BT-151 Umsatzsteuer-Kategorie-Code (UNCL 5305).
 * S=Standard, Z=Zero-rated, E=Exempt, AE=Reverse charge, K=Intra-community, G=Export.
 */
export type Bt151CategoryCode = 'S' | 'Z' | 'E' | 'AE' | 'K' | 'G';

/** Alias für die Verwendung im `InvoiceItem`-Interface (BT-151). */
export type InvoiceTaxCategoryCode = Bt151CategoryCode;

/**
 * BT-151-Codes mit Nullsatz/Befreiung — der USt-Satz MUSS 0 sein (BR-*-09).
 * Nur `S` (Standard) trägt einen positiven Satz (19/7).
 */
const ZERO_RATE_CATEGORY_CODES: ReadonlySet<Bt151CategoryCode> = new Set<Bt151CategoryCode>([
  'Z',
  'E',
  'AE',
  'K',
  'G',
]);

/**
 * Rate-Constraint für einen BT-151-Code (`S|Z|E|AE|K|G`).
 *
 * Für Oberflächen, die direkt mit dem BT-151-Code arbeiten (z. B. der
 * Recurring-Vorlagen-Editor) statt mit dem feingranularen
 * `InvoiceTaxCategoryKey`. Spiegelt exakt den Validator-Contract
 * (`ApiInvoiceValidator`): `S` → Satz 19 oder 7 (Default 19); alle
 * Nullsatz-/Befreiungs-Codes (Z/E/AE/K/G) → Satz auf 0 gesperrt.
 *
 * Fail-safe: unbekannte/leere Codes gelten als `S` (nicht gesperrt), damit
 * der Standardfall nie blockiert wird — Bestandsverhalten.
 *
 * @param code BT-151-Kategorie-Code der Position.
 * @returns `allowedRates` (erlaubte Sätze) + `ratesLocked` (true ⇒ auf 0 fixiert).
 */
export function getRateConstraintForCategoryCode(
  code: Bt151CategoryCode | string | null | undefined
): { allowedRates: number[]; ratesLocked: boolean } {
  if (code && ZERO_RATE_CATEGORY_CODES.has(code as Bt151CategoryCode)) {
    return { allowedRates: [0], ratesLocked: true };
  }
  return { allowedRates: [19, 7], ratesLocked: false };
}

/**
 * Fachlicher UI-Auswahl-Schlüssel. Verlustfreie Quelle der Nutzer-Wahl —
 * `E` ist mehrdeutig (§19 vs. §4), daher wird zusätzlich der Key persistiert.
 */
export type InvoiceTaxCategoryKey =
  | 'standard'
  | 'pv_12_3'
  | 'small_business_19'
  | 'exempt_4'
  | 'intra_community_6a'
  | 'reverse_charge_13b'
  | 'export_third_country';

/** Default-Auswahl für neue Positionen / Anzeige-Default bei Altrechnungen. */
export const DEFAULT_TAX_CATEGORY_SELECTION: InvoiceTaxCategoryKey = 'standard';

/** Platzhalter im §4-Befreiungstext, der durch die konkrete Nr. ersetzt wird. */
export const PARAGRAPH_NR_PLACEHOLDER = '{{paragraph_4_nummer}}';

/**
 * Ein Katalog-Eintrag beschreibt eine wählbare Steuerart vollständig:
 * BT-151-Code, Satz-Constraint, Befreiungsgrund-Mechanik und Pflicht-Validierungen.
 */
export interface TaxCategoryCatalogEntry {
  /** UI-Auswahl-Schlüssel (Primärschlüssel des Katalogs). */
  selection: InvoiceTaxCategoryKey;
  /** EN-16931 BT-151 Kategorie-Code. */
  bt151Code: Bt151CategoryCode;
  /**
   * Erlaubte USt-Sätze. Standard erlaubt positive Sätze (19/7);
   * alle 0 %-Kategorien sind auf `[0]` gesperrt.
   */
  allowedRates: number[];
  /**
   * true → der USt-Satz wird auf `allowedRates[0]` (0) gesperrt/gesetzt.
   * false → Nutzer wählt den Satz frei (Standard 19/7).
   */
  ratesLocked: boolean;
  /**
   * Kanonischer deutscher Pflicht-Wortlaut (BT-121). `null` = kein Befreiungsgrund
   * (S = Standard; Z = §12(3)-PV, BR-Z-10 verbietet jeden Grund).
   * Kann `PARAGRAPH_NR_PLACEHOLDER` enthalten (§4).
   */
  exemptionReasonText: string | null;
  /**
   * Optionaler, menschenlesbarer BT-22-Dokument-Hinweis (NICHT BT-120/121).
   * Nur für §12(3)-PV vorgesehen; rein informativ, kein Pflichtfeld.
   */
  bt22HintText: string | null;
  /** EN-VATEX-Code (BT-120), wo ein sauberer EU-Code existiert. Sonst `null`. */
  vatexCode: string | null;
  /** true → Käufer-USt-IdNr. ist fail-closed Pflicht (jurist A1: K/AE). */
  requiresBuyerVatId: boolean;
  /** true → konkrete §4-Nr. ist fail-closed Pflicht (jurist A2: §4). */
  requiresParagraphNr: boolean;
  /** Hinweis auf das DATEV/SKR-Erlöskonto (M7-Vorbereitung, nicht VB-1-blockierend). */
  skrHint: string;
}

/**
 * Verbindliche Katalog-Tabelle (MVP). Reihenfolge = Anzeigereihenfolge im Selektor.
 */
export const TAX_CATEGORY_CATALOG: Record<InvoiceTaxCategoryKey, TaxCategoryCatalogEntry> = {
  standard: {
    selection: 'standard',
    bt151Code: 'S',
    allowedRates: [19, 7],
    ratesLocked: false,
    exemptionReasonText: null,
    bt22HintText: null,
    vatexCode: null,
    requiresBuyerVatId: false,
    requiresParagraphNr: false,
    skrHint: '8400 / 8300 (SKR03)',
  },
  pv_12_3: {
    selection: 'pv_12_3',
    bt151Code: 'Z',
    allowedRates: [0],
    ratesLocked: true,
    // VETO-1 / A3: §12(3)-PV ist Nullsatz (Z), KEIN Befreiungsgrund (BR-Z-10).
    exemptionReasonText: null,
    bt22HintText: 'Lieferung unterliegt dem Nullsteuersatz nach § 12 Abs. 3 UStG.',
    vatexCode: null,
    requiresBuyerVatId: false,
    requiresParagraphNr: false,
    skrHint: 'PV-Erlöskonto (0 %)',
  },
  small_business_19: {
    selection: 'small_business_19',
    bt151Code: 'E',
    allowedRates: [0],
    ratesLocked: true,
    exemptionReasonText: 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmer).',
    bt22HintText: null,
    vatexCode: null,
    requiresBuyerVatId: false,
    requiresParagraphNr: false,
    skrHint: '8195 / 4185',
  },
  exempt_4: {
    selection: 'exempt_4',
    bt151Code: 'E',
    allowedRates: [0],
    ratesLocked: true,
    exemptionReasonText: `Steuerfreie Leistung gemäß § 4 Nr. ${PARAGRAPH_NR_PLACEHOLDER} UStG.`,
    bt22HintText: null,
    vatexCode: null,
    requiresBuyerVatId: false,
    requiresParagraphNr: true,
    skrHint: 'steuerfreie Erlöse',
  },
  intra_community_6a: {
    selection: 'intra_community_6a',
    bt151Code: 'K',
    allowedRates: [0],
    ratesLocked: true,
    exemptionReasonText: 'Steuerfreie innergemeinschaftliche Lieferung gemäß § 4 Nr. 1b i. V. m. § 6a UStG.',
    bt22HintText: null,
    vatexCode: 'VATEX-EU-IC',
    requiresBuyerVatId: true,
    requiresParagraphNr: false,
    skrHint: 'IGL-Konto (0 %)',
  },
  reverse_charge_13b: {
    selection: 'reverse_charge_13b',
    bt151Code: 'AE',
    allowedRates: [0],
    ratesLocked: true,
    // jurist §1.1: FIXER Wortlaut (§14a Abs. 5 S. 2 UStG), keine Paraphrase.
    exemptionReasonText: 'Steuerschuldnerschaft des Leistungsempfängers',
    bt22HintText: null,
    vatexCode: 'VATEX-EU-AE',
    requiresBuyerVatId: true,
    requiresParagraphNr: false,
    skrHint: '§13b-Konto (0 %)',
  },
  export_third_country: {
    selection: 'export_third_country',
    bt151Code: 'G',
    allowedRates: [0],
    ratesLocked: true,
    exemptionReasonText: 'Steuerfreie Ausfuhrlieferung gemäß § 4 Nr. 1a i. V. m. § 6 UStG.',
    bt22HintText: null,
    vatexCode: 'VATEX-EU-G',
    requiresBuyerVatId: false,
    requiresParagraphNr: false,
    skrHint: 'Ausfuhr-Konto (0 %)',
  },
};

/** Stabile Reihenfolge aller Auswahl-Schlüssel (für Dropdown-Rendering). */
export const TAX_CATEGORY_KEYS: InvoiceTaxCategoryKey[] = [
  'standard',
  'pv_12_3',
  'small_business_19',
  'exempt_4',
  'intra_community_6a',
  'reverse_charge_13b',
  'export_third_country',
];

/**
 * Liefert den Katalog-Eintrag zu einem Auswahl-Schlüssel.
 * @param key UI-Auswahl-Schlüssel (oder unbekannter/leerer Wert).
 * @returns Eintrag oder `undefined`, wenn der Schlüssel nicht im Katalog ist.
 */
export function getTaxCategoryEntry(
  key: InvoiceTaxCategoryKey | string | null | undefined
): TaxCategoryCatalogEntry | undefined {
  if (!key) return undefined;
  return TAX_CATEGORY_CATALOG[key as InvoiceTaxCategoryKey];
}

/**
 * Prüft, ob eine Auswahl auf 0 % gesperrt ist (alle außer `standard`).
 * Fail-safe: unbekannte Schlüssel gelten als nicht gesperrt (Bestandsverhalten).
 * @param key UI-Auswahl-Schlüssel.
 */
export function isZeroRatedSelection(key: InvoiceTaxCategoryKey | string | null | undefined): boolean {
  const entry = getTaxCategoryEntry(key);
  return entry ? entry.ratesLocked : false;
}

/**
 * Prüft, ob eine Position mit 0 %-Satz noch eine explizite Steuerart braucht.
 *
 * Ein 0 %-Satz ist fachlich mehrdeutig (§ 12 Abs. 3 PV, § 19, § 4, § 6a IGL,
 * § 13b oder Ausfuhr) — die Default-Kategorie `standard` (BT-151 `S`) mit 0 %
 * ergäbe eine widersprüchliche, nicht-konforme Rechnung. Daher muss bei 0 % eine
 * der gesperrten Kategorien gewählt sein. Liefert `true`, solange das nicht der
 * Fall ist (⇒ Pflicht-Selektor sichtbar + Submit-Block).
 *
 * Fail-safe: Nur exakt der numerische Satz `0` löst die Pflicht aus; 19/7 und
 * unbekannte/leere Sätze (null/undefined/NaN) sind nie betroffen — der
 * Standardfall bleibt unberührt. Eine bereits gewählte 0 %-Kategorie gilt als
 * aufgelöst.
 *
 * @param taxRate USt-Satz der Position.
 * @param selection Aktuelle Steuerart-Auswahl der Position.
 * @returns `true`, wenn der Satz 0 ist und (noch) keine 0 %-Kategorie gewählt wurde.
 */
export function requiresZeroRateCategoryChoice(
  taxRate: number | null | undefined,
  selection: InvoiceTaxCategoryKey | string | null | undefined
): boolean {
  // Nur exakt der numerische Satz 0 löst die Pflichtwahl aus. null/undefined/NaN
  // NIE (Number(null)===0 hätte null sonst still als 0 % gewertet — Tester-Bug).
  if (typeof taxRate !== 'number' || !Number.isFinite(taxRate) || taxRate !== 0) return false;
  const entry = getTaxCategoryEntry(selection);
  // Aufgelöst, sobald eine 0 %-gesperrte Kategorie (Z/E/AE/K/G) gewählt ist.
  if (entry && entry.ratesLocked) return false;
  return true;
}

/**
 * Baut den finalen Befreiungsgrund-Text (BT-121) für eine Auswahl.
 * Ersetzt bei §4 den Platzhalter durch die konkrete Nummer.
 * @param key UI-Auswahl-Schlüssel.
 * @param paragraphNr Konkrete §4-Nr. (nur für `exempt_4` relevant). Leer → Platzhalter bleibt.
 * @returns Befreiungstext oder `null`, wenn die Kategorie keinen Grund trägt (S, Z).
 */
export function resolveExemptionReason(
  key: InvoiceTaxCategoryKey | string | null | undefined,
  paragraphNr?: string | null
): string | null {
  const entry = getTaxCategoryEntry(key);
  if (!entry || entry.exemptionReasonText === null) return null;
  if (!entry.requiresParagraphNr) return entry.exemptionReasonText;
  const nr = (paragraphNr ?? '').trim();
  return entry.exemptionReasonText.replace(PARAGRAPH_NR_PLACEHOLDER, nr);
}

/**
 * Extrahiert die konkrete §4-Nr. aus einem bereits aufgelösten Befreiungstext
 * (für die Wiederbefüllung des Eingabefelds beim Re-Edit).
 * @param key UI-Auswahl-Schlüssel.
 * @param exemptionReason Persistierter Befreiungstext der Position.
 * @returns Die extrahierte Nr. oder Leerstring, wenn nicht ermittelbar.
 */
export function extractParagraphNr(
  key: InvoiceTaxCategoryKey | string | null | undefined,
  exemptionReason: string | null | undefined
): string {
  const entry = getTaxCategoryEntry(key);
  if (!entry || !entry.requiresParagraphNr || !exemptionReason) return '';
  const match = exemptionReason.match(/§\s*4\s*Nr\.\s*(.+?)\s*UStG/i);
  const captured = match?.[1]?.trim();
  if (!captured || captured === PARAGRAPH_NR_PLACEHOLDER) return '';
  return captured;
}

/**
 * Ergebnis einer fail-closed-Prüfung einer einzelnen Position.
 * `valid=false` blockiert das Absenden; `reasonKey` zeigt die i18n-Meldung an.
 */
export interface TaxCategoryValidationResult {
  valid: boolean;
  /** i18n-Schlüssel (`taxCategory.validation.*`) der Blockier-Meldung, falls invalid. */
  reasonKey?: 'buyerVatIdRequired' | 'paragraphNrRequired';
}

/**
 * Validiert eine Position fail-closed gegen die Pflichtfelder ihrer Steuerart
 * (jurist A1: K/AE → Käufer-USt-IdNr.; A2: §4 → konkrete Nr.).
 * Bestandsfall (`standard`/kein Key) ist immer valide.
 * @param key UI-Auswahl-Schlüssel der Position.
 * @param ctx Kontext: vorhandene Käufer-USt-IdNr. und §4-Nr.
 * @returns Validierungsergebnis; nie eine Exception.
 */
export function validateTaxCategorySelection(
  key: InvoiceTaxCategoryKey | string | null | undefined,
  ctx: { buyerVatId?: string | null; paragraphNr?: string | null }
): TaxCategoryValidationResult {
  const entry = getTaxCategoryEntry(key);
  if (!entry) return { valid: true };

  if (entry.requiresBuyerVatId && !(ctx.buyerVatId ?? '').trim()) {
    return { valid: false, reasonKey: 'buyerVatIdRequired' };
  }
  if (entry.requiresParagraphNr && !(ctx.paragraphNr ?? '').trim()) {
    return { valid: false, reasonKey: 'paragraphNrRequired' };
  }
  return { valid: true };
}
